/**
 * One shape for every source a credential can come from.
 *
 * E-mail used to be handled inline in `useSignup` while the four social sources went through
 * a separate map, so a change to how credentials are obtained had to be made twice, in two
 * shapes. Here e-mail is a provider like any other: what differs is only the `kind` of thing
 * it hands back, and one exchanger downstream turns any of the three into a session.
 *
 * Providers are factories that take their SDK as an argument. That is what makes them
 * testable without mocking a native module, and it is what will let EPIC-004 swap an
 * implementation from "call Firebase" to "call our backend" without touching a caller.
 * See EPIC-005.
 */
/**
 * Opaque here on purpose. The credential is built by a provider and consumed by the one
 * exchanger that turns it into a session; nothing between them needs to know it is a
 * Firebase type, and keeping the name out of this file is what keeps the machine free of an
 * SDK it should not depend on.
 */
export type FirebaseCredential = unknown

export type ProviderId = 'email' | 'google' | 'apple' | 'facebook' | 'linkedin' | 'customToken'

export type ProviderExtras = {
  displayName?: string
  email?: string
  birthday?: string
}

export type Credential =
  /** Exchange with `signInWithCredential`. */
  | { kind: 'firebase'; provider: ProviderId; credential: FirebaseCredential; extras?: ProviderExtras }
  /** Exchange with `signInWithCustomToken`. */
  | { kind: 'customToken'; provider: ProviderId; token: string; extras?: ProviderExtras }
  /** Already signed in — the provider had to create or sign in to produce it. */
  | { kind: 'session'; provider: ProviderId; uid: string; extras?: ProviderExtras }

export type CredentialProvider<Input = void> = {
  id: ProviderId
  authenticate(input: Input): Promise<Credential>
}

/**
 * Why authentication did not produce a credential.
 *
 * Cancellation used to be recognised centrally, by matching the thrown code against a list
 * of five strings from four different SDKs. Each provider knows its own SDK's cancel signal;
 * nowhere else should have to.
 */
export type FailureReason =
  /** The person backed out. Not an error to report. */
  | 'cancelled'
  /** The provider could not run at all — no Play Services, no network, SDK missing. */
  | 'unavailable'
  /** The provider ran and refused — wrong password, revoked grant. */
  | 'rejected'
  /** The identity exists under a different provider and cannot be used as asked. */
  | 'conflict'

export class ProviderFailure extends Error {
  reason: FailureReason
  provider: ProviderId

  constructor(provider: ProviderId, reason: FailureReason, message?: string, options?: { cause?: unknown }) {
    super(message ?? `${provider} authentication ${reason}`, options)
    this.name = 'ProviderFailure'
    this.provider = provider
    this.reason = reason
  }
}

export function isProviderFailure(error: unknown, reason?: FailureReason): error is ProviderFailure {
  if (!(error instanceof ProviderFailure)) return false

  return reason ? error.reason === reason : true
}
